/**
 * Ekonomi (P25009-496) — the seller's orders, one row each, for one period.
 *
 * The rows are built server-side (GET /api/seller-economy/orders) by the same
 * core that builds the CSV and the documents (src/util/sellerEconomyCore.js),
 * from the Sharetribe transactions alone. This duck holds them, and runs the
 * detail panel's actions:
 *
 *   - the order confirmation resend (BR-4), whose answer replaces the row's
 *     confirmation status in place;
 *   - the "next step" actions (BR-3), each an existing process transition the
 *     seller already fires from the order page — mark-delivered for a pickup
 *     (then the capture, exactly like the order page and the Frakt booking),
 *     the same for a shipping order the buyer collected after all, once its
 *     handover is stamped (P25009-499), off-platform-payment-received for an
 *     own-payment order — or, for the
 *     payment reminder, a message on the order's own conversation, which
 *     Sharetribe mails to the buyer as it does every message.
 *
 * After an action the period is fetched again: the server is the one place
 * that decides what a row says.
 */

import { storableError } from '../../util/errors';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  getSellerEconomyOrders,
  sendOrderConfirmation,
  stampOrderHandover,
  capturePayment,
} from '../../util/api';
import { transitions as purchaseTransitions } from '../../transactions/transactionProcessPurchase';
import { svDate } from '../../util/sellerEconomyCore';

// ================ Action types ================ //

export const FETCH_ORDERS_REQUEST = 'app/SellerEconomyPage/FETCH_ORDERS_REQUEST';
export const FETCH_ORDERS_SUCCESS = 'app/SellerEconomyPage/FETCH_ORDERS_SUCCESS';
export const FETCH_ORDERS_ERROR = 'app/SellerEconomyPage/FETCH_ORDERS_ERROR';

export const SEND_CONFIRMATION_REQUEST = 'app/SellerEconomyPage/SEND_CONFIRMATION_REQUEST';
export const SEND_CONFIRMATION_SUCCESS = 'app/SellerEconomyPage/SEND_CONFIRMATION_SUCCESS';
export const SEND_CONFIRMATION_ERROR = 'app/SellerEconomyPage/SEND_CONFIRMATION_ERROR';

export const ORDER_ACTION_REQUEST = 'app/SellerEconomyPage/ORDER_ACTION_REQUEST';
export const ORDER_ACTION_SUCCESS = 'app/SellerEconomyPage/ORDER_ACTION_SUCCESS';
export const ORDER_ACTION_ERROR = 'app/SellerEconomyPage/ORDER_ACTION_ERROR';

// ================ Reducer ================ //

const initialState = {
  period: null,
  rows: [],
  fetchInProgress: false,
  fetchError: null,
  // { txId, error } of the last resend; inProgress holds the tx id.
  sendInProgress: null,
  sendError: null,
  // { txId, kind } while an action runs; the error carries the same keys.
  actionInProgress: null,
  actionError: null,
};

const replaceConfirmation = (rows, txId, confirmation) =>
  rows.map(row => (row.txId === txId && confirmation ? { ...row, confirmation } : row));

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_ORDERS_REQUEST:
      // Rows of another period must not stay on screen under the new label.
      return {
        ...state,
        period: payload,
        rows: state.period === payload ? state.rows : [],
        fetchInProgress: true,
        fetchError: null,
      };
    case FETCH_ORDERS_SUCCESS:
      if (payload.period !== state.period) return state; // a slower, older request
      return { ...state, rows: payload.rows || [], fetchInProgress: false };
    case FETCH_ORDERS_ERROR:
      if (payload.period !== state.period) return state;
      return { ...state, fetchInProgress: false, fetchError: payload.error };

    case SEND_CONFIRMATION_REQUEST:
      return { ...state, sendInProgress: payload, sendError: null };
    case SEND_CONFIRMATION_SUCCESS:
      return {
        ...state,
        sendInProgress: null,
        rows: replaceConfirmation(state.rows, payload.txId, payload.confirmation),
      };
    case SEND_CONFIRMATION_ERROR:
      return {
        ...state,
        sendInProgress: null,
        sendError: { txId: payload.txId, error: payload.error },
        rows: replaceConfirmation(state.rows, payload.txId, payload.confirmation),
      };

    case ORDER_ACTION_REQUEST:
      return { ...state, actionInProgress: payload, actionError: null };
    case ORDER_ACTION_SUCCESS:
      return { ...state, actionInProgress: null };
    case ORDER_ACTION_ERROR:
      return { ...state, actionInProgress: null, actionError: payload };

    default:
      return state;
  }
}

// ================ Thunks ================ //

/** The current Swedish month, '2026-10'. */
export const currentPeriod = (now = new Date()) => svDate(now).slice(0, 7);

const isPeriod = value => /^\d{4}(-(0[1-9]|1[0-2]))?$/.test(value || '');

export const fetchEconomyOrders = period => dispatch => {
  dispatch({ type: FETCH_ORDERS_REQUEST, payload: period });
  return getSellerEconomyOrders(period)
    .then(response => {
      dispatch({ type: FETCH_ORDERS_SUCCESS, payload: { period, rows: response.rows } });
      return response;
    })
    .catch(e => {
      dispatch({ type: FETCH_ORDERS_ERROR, payload: { period, error: storableError(e) } });
    });
};

/**
 * @param {string} txId
 * @param {object} body { to: 'buyer'|'seller'|'other', email? }
 * @returns {Promise<object>} the server's answer; rejects on a refused or
 *   failed send, with `error` as the server's code ('rate-limited', …)
 */
export const sendConfirmation = (txId, body) => dispatch => {
  dispatch({ type: SEND_CONFIRMATION_REQUEST, payload: txId });
  return sendOrderConfirmation(txId, body)
    .then(response => {
      dispatch({ type: SEND_CONFIRMATION_SUCCESS, payload: { txId, confirmation: response.confirmation } });
      return response;
    })
    .catch(e => {
      dispatch({
        type: SEND_CONFIRMATION_ERROR,
        payload: { txId, error: e?.error || 'send-failed', confirmation: e?.confirmation },
      });
      throw e;
    });
};

export const ACTION_MARK_PICKED_UP = 'markPickedUp';
export const ACTION_CUSTOMER_PICKED_UP = 'customerPickedUp';
export const ACTION_MARK_PAID = 'markPaid';
export const ACTION_REMIND_BUYER = 'remindBuyer';

/* mark-delivered, then the order page's own follow-up: capture now rather than
 * leave the money to the cron. Only a hosted Checkout Session can be captured
 * this way — an embedded PaymentIntent is the cron's (see TransactionPage
 * onApproveDelivery) — and a failed capture never makes the handover look
 * failed. */
const markDelivered = (sdk, id) =>
  sdk.transactions
    .transition({ id, transition: purchaseTransitions.MARK_DELIVERED, params: {} }, { expand: true })
    .then(async response => {
      const metadata = response?.data?.data?.attributes?.metadata || {};
      if (metadata.stripeSessionId) {
        // The server checks the order is ours and delivered, and reads the
        // payment from it.
        await capturePayment({ transactionId: id?.uuid || id }).catch(() => null);
      }
    });

/**
 * Run one of the panel's next steps for a row.
 *
 * @param {object} row      the Ekonomi row
 * @param {string} kind     one of the ACTION_* kinds
 * @param {object} [extra]  { message } for the reminder
 */
export const runOrderAction = (row, kind, extra = {}) => (dispatch, getState, sdk) => {
  const id = new sdkTypes.UUID(row.txId);
  dispatch({ type: ORDER_ACTION_REQUEST, payload: { txId: row.txId, kind } });

  let work;
  if (kind === ACTION_MARK_PICKED_UP) {
    work = markDelivered(sdk, id);
  } else if (kind === ACTION_CUSTOMER_PICKED_UP) {
    /* Stamp first, like the Frakt booking: the stamp is what makes the
     * delivered order read Upphämtad. */
    work = stampOrderHandover(row.txId).then(() => markDelivered(sdk, id));
  } else if (kind === ACTION_MARK_PAID) {
    work = sdk.transactions.transition({
      id,
      transition: purchaseTransitions.OFF_PLATFORM_PAYMENT_RECEIVED,
      params: {},
    });
  } else if (kind === ACTION_REMIND_BUYER) {
    work = sdk.messages.send({ transactionId: id, content: extra.message || '' });
  } else {
    work = Promise.reject(new Error(`Unknown action ${kind}`));
  }

  return work
    .then(() => {
      dispatch({ type: ORDER_ACTION_SUCCESS });
      const { period } = getState().SellerEconomyPage;
      if (period && kind !== ACTION_REMIND_BUYER) dispatch(fetchEconomyOrders(period));
      return { done: true };
    })
    .catch(e => {
      dispatch({ type: ORDER_ACTION_ERROR, payload: { txId: row.txId, kind, error: storableError(e) } });
      throw e;
    });
};

// ================ loadData ================ //

export const loadData = (params, search) => dispatch => {
  const query = new URLSearchParams(search || '');
  const requested = query.get('period');
  return dispatch(fetchEconomyOrders(isPeriod(requested) ? requested : currentPeriod()));
};
